import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './BookFlows.styled';
import BookAnalysesList from 'components/Analyses/BookAnalyses/List/List';
import Emails from './Emails/Emails';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useBookFlows from 'hooks/useBookFlows';
import useBookAnalyses from 'hooks/useBookAnalyses';
import { DELETE_BOOK_FLOW } from 'helpers/confirmations';
import { checkValidEmail, needToUpdateField } from 'helpers';
import { bookFlowsSelectors } from "features/bookFlows";

const BookFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getBookFlow, createBookFlow, updateBookFlow } = useBookFlows();
  const { getBookAnalyses, clearLocalBookAnalyses } = useBookAnalyses();
  const id = useCustomSelector(bookFlowsSelectors.getBookFlowId);
  const bookFlow = useCustomSelector(bookFlowsSelectors.getBookFlowData);
  const fetching = useCustomSelector(bookFlowsSelectors.bookFlowIsFetching);
  const [completionEmails, setCompletionEmails] = useState([]);
  const [failureEmails, setFailureEmails] = useState([]);
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (book_flow) => {
    setCompletionEmails(book_flow?.completion_emails);
    setFailureEmails(book_flow?.failure_emails);
  };
  const handleExited = () => {
    setCompletionEmails([]);
    setFailureEmails([]);
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    _map(completionEmails, e => {
      if (!checkValidEmail(e)) hasError = true;
    })

    _map(failureEmails, e => {
      if (!checkValidEmail(e)) hasError = true;
    })

    if (hasError) return;

    const book_flow = {
      ...(needToUpdateField(bookFlow?.completion_emails, completionEmails) && { completion_emails: completionEmails }),
      ...(needToUpdateField(bookFlow?.failure_emails, failureEmails) && { failure_emails: failureEmails }),
    };

    if (!_isEmpty(book_flow)) {
      if (!!id) {
        updateBookFlow(book_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createBookFlow(book_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_BOOK_FLOW });
  const fetchBookAnalyses = useCallback(() => {
    getBookAnalyses({ offset: 0 })
  }, [getBookAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalBookAnalyses();
    };
  }, [id, clearLocalBookAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getBookFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { book_flow } = payload;
          handleEntering(book_flow);
          fetchBookAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getBookFlow, fetchBookAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Emails emails={completionEmails} setEmails={v => setCompletionEmails(v)} title='Completion Emails' edit={edit || !id}/>
            <Emails emails={failureEmails} setEmails={v => setFailureEmails(v)} title='Failed Emails' edit={edit || !id}/>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <BookAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <BookFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);